import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Check, 
  Star, 
  ArrowRight, 
  Shield, 
  Clock, 
  Users,
  FileText,
  Sparkles,
  BarChart3,
  Settings,
  Globe,
  X,
  ChevronDown,
  ChevronUp,
  Play,
  Monitor,
  Building
} from "lucide-react";

const Pricing = () => {
  const navigate = useNavigate();
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [enterpriseData, setEnterpriseData] = useState({
    company_name: "",
    user_email: "",
    team_size: ""
  });
  const [loading, setLoading] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showBasicDemo, setShowBasicDemo] = useState(false);
  const [showProfessionalDemo, setShowProfessionalDemo] = useState(false);
  const [showEnterpriseDemo, setShowEnterpriseDemo] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== 'https://docgen-platform-1.preview.emergentagent.com' 
    ? process.env.REACT_APP_BACKEND_URL 
    : 'http://localhost:8001';

  // Function to validate work email domains
  const isWorkEmail = (email) => {
    if (!email) return false;
    
    const personalDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
      'aol.com', 'icloud.com', 'me.com', 'live.com', 'msn.com',
      'yahoo.co.uk', 'yahoo.ca', 'yahoo.fr', 'googlemail.com',
      'protonmail.com', 'tutanota.com', 'yandex.com', 'mail.ru'
    ];
    
    const domain = email.toLowerCase().split('@')[1];
    return domain && !personalDomains.includes(domain);
  };

  const handlePayPerForm = () => {
    navigate('/services');
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqData = [
    {
      question: "Can I cancel anytime?",
      answer: "Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees."
    },
    {
      question: "Do you offer refunds?",
      answer: "We offer a 30-day money-back guarantee on all paid plans. If you're not satisfied, we'll refund your payment."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, debit cards, and digital wallets through our secure payment processor."
    },
    {
      question: "Are the documents legally valid?",
      answer: "Yes, all documents are prepared according to state and federal legal requirements. However, we recommend reviewing with a qualified attorney for complex matters."
    },
    {
      question: "How quickly can I get my documents?",
      answer: "Most documents are generated instantly after completing the questionnaire. Complex documents may take a few minutes to process."
    },
    {
      question: "Do you provide legal advice?",
      answer: "No, we provide legal document preparation services only. We are not a law firm and cannot provide legal advice. For legal advice, please consult with a qualified attorney."
    },
    {
      question: "What states are supported?",
      answer: "We support all 50 US states, with state-specific forms and requirements automatically applied to your documents."
    },
    {
      question: "Is my information secure?",
      answer: "Yes, we use bank-level encryption to protect your personal information. All data is stored securely and never shared with third parties."
    }
  ];

  const handleProfessionalService = () => {
    navigate('/services');
  };

  const handleEnterpriseStart = async () => {
    if (!enterpriseData.company_name || !enterpriseData.user_email) {
      alert('Please fill in company name and email address');
      return;
    }

    // Validate work email for Enterprise plan
    if (!isWorkEmail(enterpriseData.user_email)) {
      alert('Enterprise plan requires a work email address. Please use your company email instead of personal email providers (Gmail, Yahoo, Hotmail, etc.)');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/api/enterprise/start-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...enterpriseData,
          team_size: parseInt(enterpriseData.team_size) || 1
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`${data.message}\n\nTrial Details:\n• Duration: ${data.trial_details.duration}\n• Team Size: ${data.trial_details.team_size} users\n• Support: ${data.trial_details.support_level}\n• Features: Unlimited documents, team collaboration, analytics & more`);
        setShowEnterpriseModal(false);
        setEnterpriseData({ company_name: "", user_email: "", team_size: "" });
      } else {
        alert('Error starting enterprise trial. Please try again.');
      }
    } catch (error) {
      console.error('Enterprise trial error:', error);
      alert('Error starting enterprise trial. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const plans = [
    {
      name: "Basic",
      description: "Best for occasional users who only need one or two documents",
      price: "$49-299",
      period: "/per form",
      features: [
        "One-time payment per form",
        "Access to individual legal/business forms",
        "Secure download and storage",
        "Step-by-step guided form completion",
        "Email support"
      ],
      cta: "Browse Forms",
      popular: false,
      ctaVariant: "outline"
    },
    {
      name: "Professional",
      description: "Ideal for individuals and small businesses",
      price: "$39.99",
      period: "/month",
      features: [
        "Unlimited access to all standard forms",
        "Automatic updates to documents as laws/regulations change",
        "Save, edit, and reuse templates anytime",
        "E-signature integration included",
        "Priority customer support (chat & email)"
      ],
      cta: "Get Started",
      popular: true,
      ctaVariant: "default"
    },
    {
      name: "Enterprise",
      description: "Perfect for real estate companies, financial services, and growing businesses",
      price: "$99.99",
      period: "/month",
      features: [
        "Everything in Professional",
        "Unlimited access to all standard forms",
        "Automatic updates to documents as laws/regulations change", 
        "Save, edit, and reuse templates anytime",
        "E-signature integration included",
        "Priority customer support (chat & email)",
        "Shared team document library",
        "Advanced collaboration tools (comments, version history)",
        "Dedicated account manager",
        "7-day free trial"
      ],
      cta: "Get Started",
      popular: false,
      ctaVariant: "default"
    }
  ];

  const savingsData = [
    {
      service: "Will & Testament",
      traditional: "$1,200-$2,500",
      formforgepro: "$49.00 per form - $99.00 per month",
      savings: "94-98%"
    },
    {
      service: "Trust Creation",
      traditional: "$3,000-$8,000", 
      formforgepro: "$49.00 per form - $99.00 per month",
      savings: "96-98%"
    },
    {
      service: "Estate Planning",
      traditional: "$2,500-$6,000",
      formforgepro: "$49.00 per form - $99.00 per month", 
      savings: "94-98%"
    },
    {
      service: "Business Contract",
      traditional: "$2,000-$5,000",
      formforgepro: "$49.00 per form - $99.00 per month",
      savings: "95-98%"
    },
    {
      service: "Non-Disclosure Agreement",
      traditional: "$600-$1,200",
      formforgepro: "$49.00 per form - $99.00 per month",
      savings: "82-92%"
    },
    {
      service: "Power of Attorney", 
      traditional: "$800-$1,800",
      formforgepro: "$49.00 per form - $99.00 per month",
      savings: "88-94%"
    },
    {
      service: "Employment Agreement",
      traditional: "$1,500-$3,000",
      formforgepro: "$49.00 per form - $99.00 per month",
      savings: "93-97%"
    },
    {
      service: "Provisional Patent Application",
      traditional: "$2,500-$8,000",
      formforgepro: "$49.00 per form - $99.00 per month",
      savings: "96-98%"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-teal-100 text-teal-800 hover:bg-teal-200">
            Transparent Pricing
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Simple, Honest Pricing
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Choose the plan that fits your needs. No hidden fees, no surprises.
          </p>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative hover:shadow-lg transition-shadow ${
                  plan.popular ? 'ring-2 ring-yellow-500 shadow-lg' : ''
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-teal-500 text-white">
                    <Star className="w-4 h-4 mr-1" />
                    Most Popular
                  </Badge>
                )}
                
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl font-bold text-gray-800">
                    {plan.name}
                  </CardTitle>
                  <p className="text-gray-600 mt-2">{plan.description}</p>
                  <div className="mt-6">
                    <div className="text-4xl font-bold text-gray-800">
                      {plan.price}
                      <span className="text-lg font-normal text-gray-600">{plan.period}</span>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                        <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <div className="space-y-3">
                    <Button 
                      onClick={() => {
                        if (plan.name === "Basic") {
                          handlePayPerForm();
                        } else if (plan.name === "Professional") {
                          handleProfessionalService();
                        } else if (plan.name === "Enterprise") {
                          setShowEnterpriseModal(true);
                        }
                      }}
                      disabled={loading}
                      className={`w-full ${
                        plan.popular 
                          ? 'bg-teal-500 hover:bg-teal-600 text-white' 
                          : plan.ctaVariant === 'outline'
                          ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          : 'bg-gray-800 hover:bg-gray-900 text-white'
                      }`}
                      variant={plan.ctaVariant}
                    >
                      {loading ? "Loading..." : plan.cta}
                    </Button>
                    
                    {/* Demo buttons for each plan */}
                    {plan.name === "Basic" && (
                      <Button 
                        variant="outline"
                        onClick={() => setShowBasicDemo(true)}
                        disabled={loading}
                        className="w-full border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Watch Demo
                      </Button>
                    )}
                    
                    {plan.name === "Professional" && (
                      <Button 
                        variant="outline"
                        onClick={() => setShowProfessionalDemo(true)}
                        disabled={loading}
                        className="w-full border-teal-300 text-teal-600 hover:bg-teal-50"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Watch Demo
                      </Button>
                    )}
                    
                    {plan.name === "Enterprise" && (
                      <Button 
                        variant="outline"
                        onClick={() => setShowEnterpriseDemo(true)}
                        disabled={loading}
                        className="w-full border-gray-700 text-gray-700 hover:bg-gray-50"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Watch Demo
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect Organizations Section */}
      <section className="bg-gradient-to-br from-teal-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-6 bg-teal-100 text-teal-800 hover:bg-teal-200">
              Perfect Organizations
            </Badge>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Trusted by Leading Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              FormForgePro is the perfect solution for organizations that handle complex legal documentation regularly
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Real Estate Companies */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-gray-800">
                  Real Estate Companies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4 text-center">
                  Streamline property transactions, contracts, and compliance documentation
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Purchase & Sale Agreements
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Lease Agreements & Amendments
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Property Management Documents
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Non-Disclosure Agreements
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Power of Attorney Forms
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Financial Services */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-gray-800">
                  Financial Services
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4 text-center">
                  Ensure compliance and streamline client onboarding with professional documentation
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Investment Advisory Agreements
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Client Onboarding Documents
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Compliance & Regulatory Forms
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Trust & Estate Planning
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                    Business Formation Documents
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center bg-white rounded-lg p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Why These Industries Choose FormForgePro
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-teal-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Compliance Ready</h4>
                <p className="text-sm text-gray-600">Documents updated with latest regulations and state requirements</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-teal-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Time Savings</h4>
                <p className="text-sm text-gray-600">Generate professional documents in minutes, not hours</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">Team Collaboration</h4>
                <p className="text-sm text-gray-600">Share templates and collaborate seamlessly across departments</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              Join hundreds of real estate firms and financial service companies who have streamlined their legal documentation process with FormForgePro
            </p>
            <Button 
              size="lg"
              onClick={() => setShowEnterpriseModal(true)}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3"
            >
              Start Enterprise Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              See Your Savings
            </h2>
            <p className="text-xl text-gray-600">
              Compare traditional legal fees with FormForgePro pricing
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">Service</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">Traditional Legal Fees</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">FormForgePro Price</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">Your Savings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {savingsData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-800 font-medium">{row.service}</td>
                      <td className="px-6 py-4 text-sm text-red-600 font-medium">{row.traditional}</td>
                      <td className="px-6 py-4 text-sm text-green-600 font-medium">{row.formforgepro}</td>
                      <td className="px-6 py-4 text-sm text-green-700 font-bold">{row.savings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              What's Included
            </h2>
            <p className="text-xl text-gray-600">
              All plans include core legal document preparation features
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="p-3 bg-teal-100 rounded-lg w-fit mx-auto mb-4">
                  <Shield className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">Bank-Level Security</h3>
                <p className="text-sm text-gray-600">Your data is protected with enterprise-grade security</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <div className="p-3 bg-teal-100 rounded-lg w-fit mx-auto mb-4">
                  <Globe className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">Multi-Jurisdiction</h3>
                <p className="text-sm text-gray-600">Support for all US states and Canadian provinces</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <div className="p-3 bg-teal-100 rounded-lg w-fit mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">AI-Powered</h3>
                <p className="text-sm text-gray-600">Advanced AI ensures accuracy and compliance</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <div className="p-3 bg-teal-100 rounded-lg w-fit mx-auto mb-4">
                  <Clock className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">24/7 Access</h3>
                <p className="text-sm text-gray-600">Create documents anytime, anywhere</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">
              Click on any question to see the answer
            </p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <Card key={index} className="border border-gray-200 hover:border-teal-300 transition-colors">
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-800 pr-4">
                      {faq.question}
                    </h3>
                    {openFAQ === index ? (
                      <ChevronUp className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFAQ === index && (
                    <div className="px-6 pb-6 pt-0">
                      <div className="border-t border-gray-100 pt-4">
                        {faq.question === "Do you offer refunds?" ? (
                          <p className="text-gray-600 leading-relaxed">
                            We offer a 30-day money-back guarantee on all paid plans. If you're not satisfied, we'll refund your payment. For complete details about our refund policy, including eligibility requirements and processing times, {' '}
                            <Link 
                              to="/refund-policy" 
                              className="text-teal-600 hover:text-teal-700 underline font-medium"
                            >
                              read more
                            </Link>.
                          </p>
                        ) : (
                          <p className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <Button 
              asChild
              variant="outline"
              className="border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              <Link to="/help">Contact Support</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Save Thousands?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Browse our legal forms and pay only for what you need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={handlePayPerForm}
              disabled={loading}
              className="bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-3"
            >
              {loading ? "Loading..." : "Browse Legal Forms"}
            </Button>
            <Button 
              variant="outline"
              size="lg"
              asChild
              className="border-white text-white hover:bg-white hover:text-gray-800 px-8 py-3"
            >
              <Link to="/services">See our plans</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Enterprise Modal */}
      {showEnterpriseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Start Enterprise Trial</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowEnterpriseModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Start your 7-day free enterprise trial with unlimited documents and team collaboration.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <Input
                  type="text"
                  value={enterpriseData.company_name}
                  onChange={(e) => setEnterpriseData({...enterpriseData, company_name: e.target.value})}
                  placeholder="Enter your company name"
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Email Address *
                </label>
                <Input
                  type="email"
                  value={enterpriseData.user_email}
                  onChange={(e) => setEnterpriseData({...enterpriseData, user_email: e.target.value})}
                  placeholder="Enter your company email address"
                  className={`w-full ${
                    enterpriseData.user_email && !isWorkEmail(enterpriseData.user_email) 
                      ? 'border-red-300 focus:border-red-500' 
                      : ''
                  }`}
                />
                {enterpriseData.user_email && !isWorkEmail(enterpriseData.user_email) && (
                  <p className="text-red-600 text-xs mt-1">
                    Enterprise plan requires a work email. Personal emails (Gmail, Yahoo, etc.) are not accepted.
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Size
                </label>
                <Input
                  type="number"
                  value={enterpriseData.team_size}
                  onChange={(e) => setEnterpriseData({...enterpriseData, team_size: e.target.value})}
                  placeholder="Number of team members"
                  className="w-full"
                />
              </div>
              
              <div className="bg-yellow-50 p-3 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Enterprise Trial Includes:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Unlimited document generation</li>
                  <li>• Team collaboration workspace</li>
                  <li>• Advanced analytics and reporting</li>
                  <li>• Priority support</li>
                  <li>• Custom branding options</li>
                </ul>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={handleEnterpriseStart}
                  disabled={loading || !enterpriseData.company_name || !enterpriseData.user_email}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  {loading ? "Starting..." : "Start Enterprise Trial"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowEnterpriseModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Basic Demo Modal */}
      {showBasicDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                <FileText className="w-6 h-6 mr-3 text-blue-600" />
                Basic Plan Demo - Pay Per Form
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowBasicDemo(false)}
                className="hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h4 className="text-lg font-semibold text-blue-800 mb-2">
                  🏠 Perfect for Individuals & Occasional Document Needs
                </h4>
                <p className="text-blue-700">
                  Need just one or two legal documents? Our pay-per-form option is perfect for 
                  personal use, one-time transactions, or occasional business needs.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Simple Workflow */}
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    3-Step Simple Process
                  </h5>
                  <div className="space-y-3">
                    <div className="flex items-center p-3 bg-white rounded border border-blue-200">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
                      <div>
                        <strong className="text-blue-800">Choose Your Document</strong>
                        <p className="text-sm text-gray-600">Select from our library of legal forms</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded border border-blue-200">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
                      <div>
                        <strong className="text-blue-800">Fill Simple Form</strong>
                        <p className="text-sm text-gray-600">Answer easy questions in 5-10 minutes</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded border border-blue-200">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                      <div>
                        <strong className="text-blue-800">Download & Use</strong>
                        <p className="text-sm text-gray-600">Get your professional document instantly</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Perfect For */}
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-green-800 mb-3">Perfect for:</h5>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                      <div>
                        <strong className="text-green-800">Personal Use</strong>
                        <p className="text-sm text-gray-600">Wills, power of attorney, personal contracts</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                      <div>
                        <strong className="text-green-800">One-Time Transactions</strong>
                        <p className="text-sm text-gray-600">Property sales, freelance contracts</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                      <div>
                        <strong className="text-green-800">Small Transactions</strong>
                        <p className="text-sm text-gray-600">NDAs, simple agreements, forms</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-yellow-800 mb-3">Sample Pricing</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Simple Will</span>
                      <strong>$49</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>NDA Agreement</span>
                      <strong>$79</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Power of Attorney</span>
                      <strong>$99</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Business Contract</span>
                      <strong>$149</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-800 mb-3">
                No Monthly Commitment Required
              </h4>
              <p className="text-gray-600 mb-4">
                Pay only for what you need. Professional documents ready in minutes.
              </p>
              <Button 
                size="lg"
                onClick={() => {
                  setShowBasicDemo(false);
                  handlePayPerForm();
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
              >
                Browse Legal Forms
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Professional Demo Modal */}
      {showProfessionalDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                <Users className="w-6 h-6 mr-3 text-teal-600" />
                Professional Plan Demo - For Small Businesses
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowProfessionalDemo(false)}
                className="hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gradient-to-r from-teal-50 to-green-50 p-4 rounded-lg border border-teal-200">
                <h4 className="text-lg font-semibold text-teal-800 mb-2">
                  🚀 Perfect for Entrepreneurs & Small Business Owners
                </h4>
                <p className="text-teal-700">
                  Running a business means you need documents regularly. Get unlimited access 
                  to all our forms for less than the cost of a single lawyer consultation.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="bg-teal-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-teal-800 mb-3">Unlimited Business Documents</h5>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {[
                      "Employment Contracts",
                      "Client Agreements", 
                      "Partnership Docs",
                      "NDAs & Confidentiality",
                      "Service Agreements",
                      "Vendor Contracts",
                      "Lease Agreements",
                      "Terms of Service"
                    ].map((doc, i) => (
                      <div key={i} className="flex items-center">
                        <Check className="w-4 h-4 text-green-600 mr-2" />
                        <span>{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-green-800 mb-3">Template Management</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <Settings className="w-4 h-4 text-green-600 mr-2" />
                      <span>Save your business info for auto-population</span>
                    </div>
                    <div className="flex items-center">
                      <Settings className="w-4 h-4 text-green-600 mr-2" />
                      <span>Reuse templates with updated terms</span>
                    </div>
                    <div className="flex items-center">
                      <Settings className="w-4 h-4 text-green-600 mr-2" />
                      <span>Version control and document history</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-blue-800 mb-3">ROI Calculator</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>1 Employment Contract (Lawyer)</span>
                      <span className="text-red-600">$500</span>
                    </div>
                    <div className="flex justify-between">
                      <span>1 Service Agreement (Lawyer)</span>
                      <span className="text-red-600">$400</span>
                    </div>
                    <div className="flex justify-between">
                      <span>1 NDA (Lawyer)</span>
                      <span className="text-red-600">$300</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between font-bold">
                      <span>Total for 3 Documents</span>
                      <span className="text-red-600">$1,200</span>
                    </div>
                    <div className="text-center py-2">
                      <Badge className="bg-teal-600 text-white text-lg px-4 py-2">
                        FormForgePro: $39.99/month
                      </Badge>
                    </div>
                    <div className="text-center">
                      <span className="text-green-600 font-bold">Save $1,160+ per month!</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center bg-gradient-to-r from-teal-50 to-green-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-800 mb-3">
                Start Growing Your Business Today
              </h4>
              <p className="text-gray-600 mb-4">
                Join thousands of entrepreneurs who trust FormForgePro for their business documents
              </p>
              <Button 
                size="lg"
                onClick={() => {
                  setShowProfessionalDemo(false);
                  handleProfessionalService();
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3"
              >
                Start Professional Plan - $39.99/month
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Enterprise Demo Modal (Law Firm Focused) */}
      {showEnterpriseDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-3xl font-bold text-gray-900 flex items-center">
                <Monitor className="w-8 h-8 mr-3 text-purple-600" />
                Enterprise Demo - For Teams & Law Firms
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowEnterpriseDemo(false)}
                className="hover:bg-gray-100"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
            
            <div className="mb-6">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                <h4 className="text-lg font-semibold text-purple-800 mb-2">
                  🏛️ Perfect for Law Firms, Legal Teams & Growing Businesses
                </h4>
                <p className="text-purple-700">
                  Advanced collaboration, team management, and professional features designed 
                  for legal practices and businesses with multiple team members.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                  <h5 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-purple-600" />
                    Advanced Workflow Management
                  </h5>
                  
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border border-purple-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Team Document Library</span>
                        <Badge className="bg-purple-100 text-purple-800">Active</Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Shared templates and client docs</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Version Control</span>
                        <Badge variant="outline">Collaboration</Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Track changes and approvals</p>
                    </div>
                    
                    <div className="bg-white p-3 rounded border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Client Portal</span>
                        <Badge variant="outline">Premium</Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Secure client document sharing</p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h5 className="font-semibold text-yellow-800 mb-3 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-yellow-600" />
                    Team Collaboration
                  </h5>
                  <div className="text-sm text-yellow-700 space-y-2">
                    <div className="flex items-center">
                      <Check className="w-4 h-4 mr-2 text-green-600" />
                      Real-time document editing and comments
                    </div>
                    <div className="flex items-center">
                      <Check className="w-4 h-4 mr-2 text-green-600" />
                      Role-based permissions and access control
                    </div>
                    <div className="flex items-center">
                      <Check className="w-4 h-4 mr-2 text-green-600" />
                      Dedicated account manager support
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-blue-600" />
                    Perfect for Legal Professionals
                  </h5>
                  <div className="space-y-3 text-sm">
                    <div className="bg-white p-3 rounded border">
                      <strong className="text-blue-800">Law Firms (5+ Attorneys)</strong>
                      <p className="text-gray-600 mt-1">Standardize processes across your practice</p>
                    </div>
                    <div className="bg-white p-3 rounded border">
                      <strong className="text-blue-800">Legal Departments</strong>
                      <p className="text-gray-600 mt-1">Enterprise-grade compliance and security</p>
                    </div>
                    <div className="bg-white p-3 rounded border">
                      <strong className="text-blue-800">Growing Businesses</strong>
                      <p className="text-gray-600 mt-1">Scale legal operations with your growth</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h5 className="font-semibold text-green-800 mb-3">Enterprise ROI</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Legal Staff Time Savings</span>
                      <span className="font-semibold text-green-600">40+ hrs/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Document Creation Cost</span>
                      <span className="font-semibold text-red-600">-85%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Client Response Time</span>
                      <span className="font-semibold text-green-600">-75%</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span className="text-green-800">Monthly Savings</span>
                      <span className="text-green-600">$3,000+</span>
                    </div>
                    <div className="text-center">
                      <Badge className="bg-green-600 text-white">ROI: 3,000%</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h5 className="font-semibold text-gray-800 mb-4">Enterprise Legal Document Suite</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  "Client Retainer Agreements",
                  "Complex Business Contracts", 
                  "Multi-Party Agreements",
                  "Regulatory Compliance Docs",
                  "Corporate Resolutions",
                  "Partnership Agreements",
                  "M&A Documentation",
                  "Employment Law Suite"
                ].map((doc, index) => (
                  <div key={index} className="bg-white p-3 rounded border border-purple-200 text-center">
                    <FileText className="w-5 h-5 mx-auto mb-2 text-purple-600" />
                    <span className="text-xs font-medium text-gray-700">{doc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200">
              <h4 className="text-xl font-bold text-gray-800 mb-3">
                Transform Your Legal Operations
              </h4>
              <p className="text-gray-600 mb-4">
                Join leading law firms and enterprises who trust FormForgePro for their document needs
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  size="lg"
                  onClick={() => {
                    setShowEnterpriseDemo(false);
                    setShowEnterpriseModal(true);
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
                >
                  Start 7-Day Enterprise Trial
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    setShowEnterpriseDemo(false);
                    handlePayPerForm();
                  }}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-3"
                >
                  Schedule Live Demo
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                7-day free trial • No credit card required • Full enterprise features • Dedicated support
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;